import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
import missao640 from "@/assets/pai-missao-640.webp";
import missao1024 from "@/assets/pai-missao-1024.webp";
import missao1600 from "@/assets/pai-missao-1600.webp";
import missao1920 from "@/assets/pai-missao-1920.webp";
import fatherSon1024 from "@/assets/father-son-sunset-1024.webp";
import fatherSon1600 from "@/assets/father-son-sunset-1600.webp";
import fatherSon1920 from "@/assets/father-son-sunset-1920.webp";
import {
  ArrowDown,
  Check,
  ShieldCheck,
  Heart,
  Compass,
  Hammer,
  Flame,
  Crown,
  Gift,
  Clock,
  Users,
  BookOpen,
  Target,
  ChevronDown,
} from "lucide-react";

const LeadCaptureModal = lazy(() => import("@/components/lead-capture-modal"));

export const Route = createFileRoute("/paimemoravel-v2")({
  head: () => ({
    meta: [
      { title: "Deus confiou uma missão — O Pai que Seu Filho Vai Lembrar™" },
      {
        name: "description",
        content:
          "Um caminho para pais cristãos que desejam transformar amor em presença, direção e legado. Treinamento O Pai que Seu Filho Vai Lembrar™ por R$497.",
      },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Deus não apenas lhe deu um filho. Ele confiou uma missão." },
      {
        property: "og:description",
        content:
          "Criar filhos nunca foi apenas uma responsabilidade. É um chamado para formar homens de fé, caráter e propósito.",
      },
      { property: "og:type", content: "product" },
      { property: "og:url", content: "https://presencaquemarca.com.br/paimemoravel-v2" },
      { property: "og:image", content: `https://presencaquemarca.com.br${missao1600}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://presencaquemarca.com.br${missao1600}` },
    ],
    links: [
      { rel: "canonical", href: "https://presencaquemarca.com.br/paimemoravel-v2" },
      {
        rel: "preload",
        as: "image",
        href: missao1024,
        imageSrcSet: `${missao640} 640w, ${missao1024} 1024w, ${missao1600} 1600w, ${missao1920} 1920w`,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
  }),
  component: PaiMemoravelV2Page,
});

// Global helper so any CTA (including the fixed mobile bar) can open the modal.
let openLeadModalFn: (() => void) | null = null;
function openLeadModal() {
  if (openLeadModalFn) openLeadModalFn();
}

function GoldButton({
  children,
  large = false,
  dataEvent = "cta_comprar_pai_memoravel",
}: {
  children: React.ReactNode;
  large?: boolean;
  dataEvent?: string;
}) {
  return (
    <button
      type="button"
      onClick={openLeadModal}
      data-event={dataEvent}
      className={`group relative inline-flex max-w-full items-center justify-center gap-3 rounded-sm gradient-gold text-center font-sans font-semibold uppercase leading-tight tracking-[0.16em] text-navy-deep shadow-gold transition-all hover:scale-[1.02] hover:shadow-[0_25px_70px_-15px_oklch(0.78_0.13_82_/_0.7)] ${
        large ? "px-7 py-5 text-[13px] md:px-12 md:py-6 md:text-base" : "px-6 py-4 text-xs md:px-8 md:py-4 md:text-sm"
      }`}
    >
      <span>{children}</span>
      <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-6 flex items-center justify-center gap-3">
      <span className="h-px w-10 bg-gold/60" />
      <span className="font-sans text-[11px] uppercase tracking-[0.4em] text-gold">{children}</span>
      <span className="h-px w-10 bg-gold/60" />
    </div>
  );
}

const preparedFor = [
  "Para trabalhar.",
  "Para construir uma carreira.",
  "Para pagar contas.",
  "Para proteger sua família.",
  "Para tomar decisões difíceis.",
];

const salmo = [
  { title: "Herança", verse: "“Os filhos são herança do Senhor...”", icon: Crown },
  { title: "Recompensa", verse: "“...os frutos do ventre, uma recompensa.”", icon: Gift },
  { title: "Flechas", verse: "“Como flechas nas mãos do guerreiro são os filhos...”", icon: Target },
  { title: "Aljava", verse: "“Bem-aventurado o homem que tem a sua aljava cheia deles.”", icon: BookOpen },
];

const vozes = ["Escola.", "Amigos.", "Internet.", "Cultura.", "Redes sociais."];

const oQueEleVe = [
  "Como você trata a mãe dele.",
  "Como reage quando algo dá errado.",
  "Como trabalha.",
  "Como lida com dinheiro.",
  "Como fala com outras pessoas.",
  "Como pede perdão.",
  "Como vive sua fé.",
  "Como reage quando ninguém está olhando.",
];

const pilares = [
  { title: "Presença", desc: "Estar verdadeiramente disponível nos momentos que importam.", icon: Heart },
  { title: "Identidade", desc: "Ajudar o filho a compreender quem ele é e quais valores carrega.", icon: Compass },
  { title: "Fé", desc: "Transformar espiritualidade em algo vivido dentro de casa.", icon: Flame },
  { title: "Conexão", desc: "Criar um ambiente em que conversas importantes realmente acontecem.", icon: Users },
  { title: "Direção", desc: "Preparar o filho para fazer escolhas mesmo quando o pai não estiver ao lado.", icon: Target },
  { title: "Legado", desc: "Construir algo que permaneça muito além da infância.", icon: Crown },
];

const diasComuns = [
  "na mesa;",
  "no carro;",
  "antes de dormir;",
  "num sábado sem programação;",
  "numa conversa inesperada;",
  "num pedido de desculpas;",
  "num abraço;",
  "numa oração.",
];

const movimentos = [
  { n: "01", title: "Conectar", icon: Heart },
  { n: "02", title: "Influenciar", icon: Compass },
  { n: "03", title: "Formar", icon: Hammer },
  { n: "04", title: "Marcar", icon: Flame },
  { n: "05", title: "Legar", icon: Crown },
];

const bonus = [
  {
    n: "Bônus 1",
    title: "Cartas de Legado™",
    desc: "Guia com livro digital e caderno de aplicação para transformar sentimentos, valores, histórias, fé, princípios e aprendizados em cartas significativas para os filhos, integrado ao Método IPM™ — Presença que Marca™.",
  },
  {
    n: "Bônus 2",
    title: "Conversas que Formam Homens™",
    desc: "Guia com livro digital e baralho imprimível de perguntas e convites para conversas significativas com filhos de 4 a 15 anos, promovendo abertura, confiança, escuta, conexão, princípios, caráter, fé e direção.",
  },
  {
    n: "Bônus 3",
    title: "Diagnóstico IPM™",
    desc: "Livro digital e caderno de avaliação baseado nos cinco movimentos Enxergar, Conectar, Marcar, Modelar e Direcionar, identificando forças, vulnerabilidades e prioridades de transformação.",
  },
  {
    n: "Bônus 4",
    title: "Kit 52 Experiências Pai & Filho™",
    desc: "Livro digital de 64 páginas e baralho imprimível com 52 experiências adaptáveis para filhos de 4 a 15 anos, criando um ano de presença intencional.",
  },
  {
    n: "Bônus 5",
    title: "Plano Pai & Filho 12 Meses™",
    desc: "Livro digital e planner imprimível para planejar experiências, rituais, prioridades, acompanhamento do vínculo e registro de memórias durante 12 meses.",
  },
];

const objecoes = [
  {
    q: "“Meu filho ainda é pequeno.”",
    a: "É justamente durante a infância que muitos referenciais de segurança, identidade, fé e relacionamento começam a ser construídos. Não é preciso esperar uma crise para começar a cultivar presença.",
  },
  {
    q: "“Minha rotina é muito corrida.”",
    a: "Presença não significa abandonar responsabilidades. Significa aprender a transformar os momentos que já existem em oportunidades reais de conexão.",
  },
  {
    q: "“Eu já errei demais.”",
    a: "Nenhum pai precisa construir sua história a partir da perfeição. Relacionamentos também são fortalecidos por humildade, reconciliação, mudança e novos começos.",
  },
  {
    q: "“Não tive um bom exemplo de pai.”",
    a: "A história que você recebeu não precisa determinar integralmente a história que você transmitirá. É possível aprender uma nova maneira de exercer a paternidade.",
  },
  {
    q: "“Isso é só para quem já é muito religioso?”",
    a: "O treinamento é cristão e fala abertamente de fé, mas é prático e voltado ao dia a dia da relação entre pai e filho. Ele foi feito para pais cristãos ocupados — não exige formação teológica nem maturidade religiosa prévia.",
  },
];

function PaiMemoravelV2Page() {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    openLeadModalFn = () => setModalOpen(true);
    return () => {
      openLeadModalFn = null;
    };
  }, []);

  return (
    <main className="overflow-x-hidden bg-background text-foreground">
      {/* HERO */}
      <section className="relative min-h-[100svh] w-full overflow-hidden bg-navy-deep">
        <img
          src={missao1024}
          srcSet={`${missao640} 640w, ${missao1024} 1024w, ${missao1600} 1600w, ${missao1920} 1920w`}
          sizes="100vw"
          alt="Pai e filho caminhando juntos em direção à luz do fim de tarde"
          width={1920}
          height={1280}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-[60%_center] opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/75 via-navy-deep/55 to-navy-deep" />
        <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-5xl flex-col items-center justify-center px-6 py-24 text-center">
          <div className="animate-fade-up">
            <SectionLabel>Paternidade • Fé • Legado</SectionLabel>
            <h1 className="font-display text-[2rem] font-light leading-[1.12] text-ivory sm:text-4xl md:text-6xl lg:text-7xl">
              Deus não apenas
              <br />
              lhe deu um filho.
              <br />
              <span className="italic text-gradient-gold">Ele confiou uma missão.</span>
            </h1>
            <p className="mx-auto mt-8 max-w-2xl text-base font-light leading-relaxed text-ivory/85 md:text-lg">
              Criar filhos nunca foi apenas uma responsabilidade. É um chamado para formar homens de
              fé, caráter e propósito — antes que outras vozes definam quem eles serão.
            </p>
            <div className="mt-10">
              <GoldButton large>Quero viver essa missão</GoldButton>
            </div>
            <p className="mx-auto mt-5 max-w-md text-sm font-light text-ivory/70">
              Um caminho prático para pais que desejam transformar amor em presença, direção e legado.
            </p>
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-scroll-bounce">
            <ArrowDown className="h-6 w-6 text-gold" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* PREPARADO PARA MUITAS COISAS */}
      <section className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <SectionLabel>O Ponto Cego</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
              Você foi preparado
              <br />
              para muitas coisas.
            </h2>
          </div>

          <ul className="mx-auto mt-12 max-w-md space-y-4">
            {preparedFor.map((item) => (
              <li
                key={item}
                className="flex items-start gap-4 border-b border-border/60 pb-4 text-base font-light text-navy/85 md:text-lg"
              >
                <Check className="mt-1 h-5 w-5 flex-shrink-0 text-gold" strokeWidth={2.5} aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="mt-16 border-y border-gold/40 py-10 text-center">
            <p className="font-display text-2xl font-light italic leading-snug text-gold md:text-4xl">
              Mas quem preparou você
              <br />
              para formar um filho?
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-2xl space-y-5 text-base font-light leading-relaxed text-navy/85 md:text-lg">
            <p>Ninguém nasce sabendo ser pai.</p>
            <p>
              Muitos homens aprenderam a conquistar, prover e resolver problemas — mas nunca foram
              ensinados a lidar com uma responsabilidade ainda maior: participar da formação de outro
              ser humano.
            </p>
          </div>

          <blockquote className="mx-auto mt-14 max-w-2xl border-l-2 border-gold pl-6 md:pl-8">
            <p className="font-display text-2xl italic leading-snug text-navy md:text-3xl">
              Ser pai não é teoria. É experiência, presença e direção.
            </p>
          </blockquote>
        </div>
      </section>

      {/* SALMO 127 */}
      <section className="relative overflow-hidden bg-navy-deep px-6 py-24 text-ivory md:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_oklch(0.78_0.13_82_/_0.10),_transparent_65%)]" />
        <div className="relative z-10 mx-auto max-w-5xl">
          <div className="text-center">
            <SectionLabel>Salmo 127:3–5</SectionLabel>
            <h2 className="mx-auto max-w-3xl font-display text-3xl font-light leading-tight md:text-5xl">
              A Bíblia não trata filhos como um acidente.
              <br />
              <span className="italic text-gradient-gold">Trata como herança.</span>
            </h2>
          </div>

          <div className="mt-16 grid gap-5 sm:grid-cols-2">
            {salmo.map(({ title, verse, icon: Icon }) => (
              <div
                key={title}
                className="rounded-sm border border-gold/25 bg-navy/40 p-8 transition-colors hover:border-gold/60"
              >
                <Icon className="h-7 w-7 text-gold" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="mt-5 font-sans text-[11px] uppercase tracking-[0.4em] text-gold">{title}</h3>
                <p className="mt-3 font-display text-xl font-light italic leading-snug text-ivory/90 md:text-2xl">
                  {verse}
                </p>
              </div>
            ))}
          </div>

          <div className="mx-auto mt-16 max-w-2xl space-y-3 text-center text-lg font-light text-ivory/85 md:text-xl">
            <p>Uma flecha possui potencial.</p>
            <p>Mas potencial sem direção não define destino.</p>
            <p>Ela precisa ser preparada.</p>
            <p>Precisa ser alinhada.</p>
            <p>Precisa apontar para algum lugar.</p>
            <p>E, em determinado momento, precisa ser lançada.</p>
          </div>

          <div className="mx-auto mt-14 max-w-2xl space-y-5 text-base font-light leading-relaxed text-ivory/80 md:text-lg">
            <p>O papel do pai não é controlar para sempre a trajetória do filho.</p>
            <p>É ajudá-lo a estar preparado quando chegar a hora de seguir seu próprio caminho.</p>
          </div>

          <p className="mx-auto mt-16 max-w-3xl text-center font-display text-2xl font-light italic leading-snug text-gradient-gold md:text-4xl">
            Seus filhos não foram colocados em suas mãos por acaso.
          </p>
        </div>
      </section>

      {/* JÁ ESTÁ SENDO FORMADO */}
      <section className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <SectionLabel>A Disputa Silenciosa</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
              Seu filho já está
              <br />
              <span className="italic text-gold">sendo formado.</span>
            </h2>
          </div>

          <p className="mx-auto mt-12 max-w-2xl text-center text-base font-light leading-relaxed text-navy/85 md:text-lg">
            Todos os dias existem vozes disputando influência sobre seus valores, sua identidade e sua
            visão de mundo.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {vozes.map((v) => (
              <span
                key={v}
                className="rounded-sm border border-border bg-card px-5 py-2.5 text-sm font-light text-navy/80"
              >
                {v}
              </span>
            ))}
          </div>

          <p className="mx-auto mt-12 max-w-2xl text-center text-base font-light leading-relaxed text-navy/85 md:text-lg">
            Mas existe uma influência que nenhuma dessas vozes deveria substituir: a presença de um pai
            intencional.
          </p>

          <div className="mt-16 text-center">
            <p className="text-base font-light text-muted-foreground md:text-lg">
              A pergunta não é se seu filho receberá influência. A pergunta é:
            </p>
            <p className="mx-auto mt-6 max-w-2xl font-display text-2xl font-light leading-snug text-navy md:text-4xl">
              Quem terá mais influência sobre <span className="italic text-gold">quem ele está se tornando?</span>
            </p>
          </div>
        </div>
      </section>

      {/* O HOMEM QUE ELE VÊ */}
      <section className="gradient-navy px-6 py-24 text-ivory md:py-32">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <SectionLabel>O Homem Que Ele Vê</SectionLabel>
            <h2 className="mx-auto max-w-3xl font-display text-3xl font-light leading-tight md:text-5xl">
              Antes de escutar o que você ensina,
              <br />
              <span className="italic text-gradient-gold">ele observa como você vive.</span>
            </h2>
          </div>

          <ul className="mx-auto mt-14 grid max-w-3xl gap-x-10 gap-y-4 sm:grid-cols-2">
            {oQueEleVe.map((item) => (
              <li key={item} className="flex items-start gap-3 border-b border-ivory/10 pb-4 text-ivory/85">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gold" aria-hidden="true" />
                <span className="text-base font-light">{item}</span>
              </li>
            ))}
          </ul>

          <p className="mx-auto mt-16 max-w-2xl text-center font-display text-2xl font-light leading-snug md:text-3xl">
            Seu filho não aprende apenas com aquilo que você diz.
            <br />
            <span className="italic text-gradient-gold">Ele aprende com o homem que você é.</span>
          </p>
        </div>
      </section>

      {/* PRODUTO */}
      <section className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <SectionLabel>O Treinamento</SectionLabel>
          <h2 className="font-display text-4xl font-light italic leading-tight text-gradient-gold md:text-6xl">
            O Pai que Seu Filho
            <br />
            Vai Lembrar™
          </h2>
          <p className="mx-auto mt-8 max-w-2xl text-base font-light leading-relaxed text-navy/85 md:text-lg">
            Um caminho para pais que desejam transformar intenção em presença — e presença em legado.
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-base font-light leading-relaxed text-muted-foreground md:text-lg">
            Um programa prático para empresários cristãos que desejam construir uma relação forte,
            admirada e memorável com seus filhos.
          </p>

          <div className="mt-14 grid gap-5 text-left sm:grid-cols-2">
            {pilares.map(({ title, desc, icon: Icon }) => (
              <div
                key={title}
                className="rounded-sm border border-border bg-card p-8 transition-all hover:border-gold hover:shadow-premium"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 bg-gold/10">
                  <Icon className="h-5 w-5 text-gold" strokeWidth={1.75} aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-display text-2xl text-navy">{title}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MOVIMENTOS */}
      <section className="bg-muted/60 px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <SectionLabel>O Método</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
              Os <span className="italic text-gold">5 Movimentos</span> do Método
            </h2>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {movimentos.map(({ n, title, icon: Icon }) => (
              <div
                key={n}
                className="group rounded-sm border border-border bg-card p-8 transition-all hover:-translate-y-1 hover:border-gold hover:shadow-premium"
              >
                <div className="font-display text-5xl font-light text-gold/30 transition-colors group-hover:text-gold/60">
                  {n}
                </div>
                <Icon className="mt-6 h-8 w-8 text-gold" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="mt-4 font-display text-2xl text-navy">{title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIAS COMUNS + CTA */}
      <section className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <SectionLabel>Dias Comuns</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
              Você não precisa esperar as próximas férias
              <br />
              <span className="italic text-gold">para ser um pai memorável.</span>
            </h2>
          </div>

          <p className="mx-auto mt-12 max-w-2xl text-center text-base font-light leading-relaxed text-navy/85 md:text-lg">
            Muitos dos momentos que um filho levará para a vida não acontecem em viagens
            extraordinárias. Acontecem:
          </p>

          <ul className="mx-auto mt-10 grid max-w-2xl gap-x-10 gap-y-3 sm:grid-cols-2">
            {diasComuns.map((item) => (
              <li key={item} className="flex items-start gap-3 text-base font-light text-navy/85">
                <span className="mt-2.5 h-1 w-4 flex-shrink-0 bg-gold/70" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <p className="mx-auto mt-16 max-w-2xl text-center font-display text-2xl font-light leading-snug text-navy md:text-3xl">
            Presença não é criar momentos perfeitos.
            <br />
            <span className="italic text-gold">É perceber o valor dos momentos que já estão acontecendo.</span>
          </p>

          <div className="mt-12 text-center">
            <GoldButton large dataEvent="cta_comecar_agora">
              Quero começar agora
            </GoldButton>
          </div>
        </div>
      </section>

      {/* BÔNUS */}
      <section className="bg-navy-deep px-6 py-24 text-ivory md:py-32">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <SectionLabel>Você Também Recebe</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight md:text-5xl">
              Cinco materiais para <span className="italic text-gradient-gold">colocar em prática</span>
            </h2>
            <p className="mt-4 text-sm font-light text-ivory/75 md:text-base">
              R$ 107,60 cada — <span className="font-medium text-gradient-gold">R$ 538,00 em bônus</span> incluídos hoje.
            </p>
          </div>

          <div className="mt-16 grid gap-5 md:grid-cols-2">
            {bonus.map((b) => (
              <div
                key={b.title}
                className="flex gap-5 rounded-sm border border-gold/25 bg-navy/40 p-6 transition-colors hover:border-gold/60 md:p-8"
              >
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full gradient-gold">
                  <Gift className="h-5 w-5 text-navy-deep" aria-hidden="true" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold">{b.n}</span>
                    <span className="font-sans text-[11px] text-ivory/60">Valor: R$ 107,60</span>
                  </div>
                  <h3 className="mt-2 font-display text-xl text-ivory md:text-2xl">{b.title}</h3>
                  <p className="mt-2 text-sm font-light text-ivory/75">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OBJEÇÕES */}
      <section className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <SectionLabel>Antes de Decidir</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
              Talvez uma parte de você
              <br />
              <span className="italic text-gold">esteja pensando...</span>
            </h2>
          </div>

          <div className="mt-14 space-y-3">
            {objecoes.map((o) => (
              <details
                key={o.q}
                className="group rounded-sm border border-border bg-card transition-colors open:border-gold/60"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 font-display text-lg text-navy outline-none focus-visible:ring-2 focus-visible:ring-gold md:text-xl">
                  <span>{o.q}</span>
                  <ChevronDown
                    className="h-5 w-5 flex-shrink-0 text-gold transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <p className="border-t border-border/60 px-6 py-5 text-base font-light leading-relaxed text-navy/85">
                  {o.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* PARA QUEM É */}
      <section className="bg-muted/60 px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <SectionLabel>Para Quem É</SectionLabel>
          <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
            Feito para <span className="italic text-gold">homens como você</span>
          </h2>

          <ul className="mx-auto mt-12 max-w-xl space-y-5 text-left">
            {[
              "Pais com filhos entre 4 e 15 anos",
              "Empresários e profissionais ocupados",
              "Homens que desejam fortalecer sua influência",
              "Pais que querem ser lembrados por mais do que dinheiro",
              "Homens que desejam construir um legado familiar",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-4 border-b border-border/60 pb-5 text-base text-navy/85 md:text-lg"
              >
                <Check className="mt-1 h-5 w-5 flex-shrink-0 text-gold" strokeWidth={2.5} aria-hidden="true" />
                <span className="font-light">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* OFERTA */}
      <section id="checkout" className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-2xl">
          <div className="text-center">
            <SectionLabel>A Decisão</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
              Tudo o que <span className="italic text-gold">você recebe</span>
            </h2>
          </div>

          <div className="mt-12 rounded-sm border border-border bg-card p-8 shadow-premium md:p-12">
            <ul className="space-y-4 text-navy/90">
              <li className="flex justify-between border-b border-border/60 pb-3">
                <span className="font-light">Treinamento Completo</span>
                <span className="font-medium">R$ 497</span>
              </li>
              <li className="flex justify-between border-b border-border/60 pb-3">
                <span className="font-light">5 Bônus Exclusivos</span>
                <span className="font-medium">R$ 538</span>
              </li>
              <li className="flex justify-between pt-2 text-lg">
                <span className="font-display">Valor Total</span>
                <span className="font-display line-through opacity-60">R$ 1.035</span>
              </li>
            </ul>

            <div className="my-8 flex items-center gap-4">
              <span className="h-px flex-1 bg-gold/40" />
              <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold">
                Hoje por apenas
              </span>
              <span className="h-px flex-1 bg-gold/40" />
            </div>

            <div className="text-center">
              <div className="flex items-baseline justify-center gap-2">
                <span className="font-display text-2xl text-navy/70">R$</span>
                <span className="font-display text-7xl font-medium text-gradient-gold md:text-8xl">
                  497
                </span>
              </div>
              <p className="mt-3 text-sm font-light text-muted-foreground">
                ou <span className="font-medium text-navy">12x de R$ 51,40</span> no cartão
              </p>

              <div className="mt-8">
                <GoldButton large>Quero ser o pai que meu filho vai lembrar</GoldButton>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-gold" aria-hidden="true" /> Pagamento Seguro
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-gold" aria-hidden="true" /> Acesso Imediato
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-gold" aria-hidden="true" /> +1.000 pais
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GARANTIA */}
      <section className="bg-navy-deep px-6 py-24 text-ivory md:py-28">
        <div className="mx-auto max-w-3xl">
          <div className="grid items-center gap-12 md:grid-cols-[auto_1fr]">
            <div className="mx-auto flex h-40 w-40 flex-shrink-0 items-center justify-center rounded-full border-2 border-gold bg-navy/40 shadow-gold md:h-48 md:w-48">
              <div className="text-center">
                <ShieldCheck className="mx-auto h-10 w-10 text-gold" strokeWidth={1.5} aria-hidden="true" />
                <div className="mt-2 font-display text-3xl text-gradient-gold md:text-4xl">7</div>
                <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold">Dias</div>
              </div>
            </div>
            <div className="text-center md:text-left">
              <SectionLabel>Risco Zero</SectionLabel>
              <h2 className="font-display text-3xl font-light text-ivory md:text-4xl">
                Garantia de <span className="italic text-gold">7 Dias</span>
              </h2>
              <p className="mt-6 text-base font-light leading-relaxed text-ivory/80 md:text-lg">
                Entre, assista às primeiras aulas e conheça o método. Se perceber que o treinamento não
                é para você, basta solicitar o reembolso dentro do prazo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative overflow-hidden bg-navy-deep px-6 py-28 text-ivory md:py-40">
        <img
          src={fatherSon1600}
          srcSet={`${fatherSon1024} 1024w, ${fatherSon1600} 1600w, ${fatherSon1920} 1920w`}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/85 to-navy-deep/60" />
        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-light leading-[1.12] md:text-6xl">
            Um dia, seu filho seguirá
            <br />
            o <span className="italic">próprio caminho.</span>
          </h2>
          <p className="mx-auto mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/80 md:text-lg">
            Até lá, você tem a oportunidade de ajudá-lo a descobrir quem ele é, no que acredita e que
            tipo de homem deseja se tornar.
          </p>
          <p className="mt-12 font-display text-2xl font-light italic leading-snug text-gradient-gold md:text-4xl">
            Filhos são como flechas.
            <br />
            E hoje, elas ainda estão em suas mãos.
          </p>
          <div className="mt-12">
            <GoldButton large>Quero começar essa jornada</GoldButton>
          </div>
          <p className="mt-5 font-sans text-[11px] uppercase tracking-[0.35em] text-gold">
            Presença. Fé. Direção. Legado.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-gold/20 bg-navy-deep px-6 py-10 text-center text-xs text-ivory/70">
        <p className="font-display text-lg italic text-gold">O Pai que Seu Filho Vai Lembrar™</p>
        <p className="mt-3">© {new Date().getFullYear()} — Todos os direitos reservados.</p>
      </footer>

      {/* CTA FIXO MOBILE */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-gold/30 bg-navy-deep/95 p-3 backdrop-blur-md md:hidden">
        <button
          type="button"
          onClick={openLeadModal}
          data-event="cta_comecar_agora"
          className="flex w-full items-center justify-center gap-2 rounded-sm gradient-gold px-4 py-3.5 font-sans text-xs font-semibold uppercase tracking-[0.18em] text-navy-deep shadow-gold"
        >
          Quero viver essa missão <span aria-hidden="true">→</span>
        </button>
      </div>
      <div className="h-20 md:hidden" aria-hidden="true" />

      <Suspense fallback={null}>
        {modalOpen && (
          <LeadCaptureModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            origem="landing_paimemoravel_v2"
            pagina="/paimemoravel-v2"
          />
        )}
      </Suspense>
    </main>
  );
}